/**
 * Section 11.3. The camera, behind a port.
 *
 * Two decoders exist because `BarcodeDetector` is fast and native on Android
 * and absent on iPhone, where a WebAssembly decoder in a worker stands in. The
 * request of 4 October 2026 (second), section 5, adds zoom, pausing while a code
 * is looked up, and decoding a photograph. None of it belongs in a component.
 */

export interface ZoomRange {
  min: number;
  max: number;
  step: number;
  /**
   * The camera zooms itself. Otherwise the zoom is digital: the preview is
   * enlarged and the decoder reads a smaller part of the picture, which is as
   * good for a small code because the picture is high resolution.
   */
  hardware: boolean;
}

export interface ScanSession {
  /** Whether the current track reports torch support. */
  torchAvailable: boolean;
  setTorch(on: boolean): Promise<void>;
  zoom: ZoomRange;
  setZoom(value: number): Promise<void>;
  /** Stops decoding without stopping the camera, while a code is looked up. */
  pause(): void;
  resume(): void;
  stop(): void;
}

export interface ScanOptions {
  video: HTMLVideoElement;
  /** Called for each decoded value; the caller ignores repeats. */
  onResult: (value: string) => void;
  onError: (error: unknown) => void;
  /** How long each decode took, for the screen's own measurement. */
  onDecodeTime?: (ms: number) => void;
}

export class CameraDeniedError extends Error {
  constructor() {
    super('Camera permission was refused');
    this.name = 'CameraDeniedError';
  }
}

export interface Scanner {
  /** Whether a camera is usable at all in this browser. */
  isSupported(): boolean;
  start(options: ScanOptions): Promise<ScanSession>;
  /** Reads a QR code from a photograph, or null when there is none to read. */
  decodeImage(file: Blob): Promise<string | null>;
}
